import {
  type Components,
  type Theme,
  alpha,
  autocompleteClasses,
  buttonClasses,
  checkboxClasses,
  menuItemClasses,
  outlinedInputClasses,
  stepIconClasses
} from '@mui/material'

import typography, { fontFamily } from './typography'

const componentOverrides: Components<Theme> = {
  MuiAlert: {
    styleOverrides: {
      root: {
        '& .MuiAlertTitle-root': {
          ...typography.body2,
          fontWeight: 700,
          marginBottom: 0
        },
        '& .MuiAlert-icon': {
          alignSelf: 'center'
        }
      },
      // Standard alerts read as a tinted surface with `.dark` text in light
      // mode; dark mode inverts to `.light` text on a stronger tint so the
      // copy stays legible on dark surfaces.
      standardInfo: ({ theme }) => {
        const text = theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.dark
        return {
          color: text,
          backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.08),
          '& .MuiAlertTitle-root': { color: text },
          '& .MuiAlert-icon': { color: theme.palette.primary.main }
        }
      },
      standardWarning: ({ theme }) => {
        const text = theme.palette.mode === 'dark' ? theme.palette.warning.light : theme.palette.warning.dark
        return {
          color: text,
          '& .MuiAlertTitle-root': { color: text },
          '& .MuiAlert-icon': { color: theme.palette.warning.main }
        }
      },
      standardError: ({ theme }) => {
        const text = theme.palette.mode === 'dark' ? theme.palette.error.light : theme.palette.error.dark
        return {
          color: text,
          backgroundColor: alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.16 : 0.08),
          '& .MuiAlertTitle-root': { color: text },
          '& .MuiAlert-icon': { color: theme.palette.error.main }
        }
      }
    }
  },

  MuiAutocomplete: {
    defaultProps: {
      size: 'small'
    },
    styleOverrides: {
      listbox: ({ theme }) => ({
        [`& .${autocompleteClasses.option}[aria-selected="true"]`]: {
          backgroundColor: alpha(theme.palette.accent.main, 0.08),
          '&.Mui-focused': {
            backgroundColor: alpha(theme.palette.accent.main, 0.12)
          }
        }
      })
    }
  },

  MuiAvatar: {
    styleOverrides: {
      root: {
        fontFamily,
        fontWeight: 400,
        fontSize: '14px',
        lineHeight: 20 / 14
      }
    }
  },

  MuiButton: {
    styleOverrides: {
      contained: ({ theme }) => ({
        boxShadow: 'none',
        padding: `${theme.spacing(2, 4)}`,
        [`&.${buttonClasses.colorPrimary}:not(.Mui-disabled)`]: {
          backgroundColor: theme.palette.accent.main,
          '&:hover': {
            backgroundColor: theme.palette.accent.dark
          }
        }
      }),
      outlined: ({ theme }) => ({
        padding: `${theme.spacing(1.75, 4)}`,
        [`&.${buttonClasses.colorPrimary}:not(.Mui-disabled)`]: {
          color: theme.palette.accent.main,
          borderColor: alpha(theme.palette.accent.main, 0.5),
          '&:hover': {
            borderColor: theme.palette.accent.main,
            backgroundColor: alpha(theme.palette.accent.main, 0.04)
          }
        }
      }),
      text: ({ theme }) => ({
        [`&.${buttonClasses.colorPrimary}:not(.Mui-disabled)`]: {
          color: theme.palette.accent.main,
          '&:hover': {
            backgroundColor: alpha(theme.palette.accent.main, 0.08)
          }
        }
      })
    }
  },

  MuiCard: {
    defaultProps: {
      elevation: 0
    }
  },

  MuiCheckbox: {
    styleOverrides: {
      root: ({ theme }) => ({
        [`&.${checkboxClasses.checked}`]: {
          color: theme.palette.accent.main
        }
      })
    }
  },

  MuiCircularProgress: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: theme.palette.accent.main
      })
    }
  },

  MuiDialog: {
    styleOverrides: {
      paper: { borderRadius: '1rem' },
      paperFullScreen: { borderRadius: 0 }
    }
  },

  MuiDialogContent: {
    styleOverrides: {
      root: {
        padding: '8px 24px'
      }
    }
  },

  MuiDialogActions: {
    styleOverrides: {
      root: {
        padding: '16px 24px 24px'
      }
    }
  },

  MuiDivider: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderColor: theme.palette.divider
      })
    }
  },

  MuiFormControl: {
    defaultProps: {
      size: 'small'
    }
  },

  MuiInputBase: {
    defaultProps: {
      // This helps with some performance issues related to adding the same style tags over and over
      // when rendering many form inputs.
      disableInjectingGlobalStyles: true
    },
    styleOverrides: {
      input: ({ theme }) => ({
        '&::placeholder': {
          color: theme.palette.text.secondary
        },
        '&[type=number]': {
          MozAppearance: 'textfield'
        },
        '&::-webkit-outer-spin-button': {
          WebkitAppearance: 'none',
          margin: 0
        },
        '&::-webkit-inner-spin-button': {
          WebkitAppearance: 'none',
          margin: 0
        }
      }),
      inputAdornedEnd: {
        color: 'primary'
      }
    }
  },

  MuiOutlinedInput: {
    styleOverrides: {
      root: ({ theme }) => ({
        [`&.Mui-focused .${outlinedInputClasses.notchedOutline}`]: {
          borderColor: theme.palette.accent.main
        }
      })
    }
  },

  MuiLinearProgress: {
    styleOverrides: {
      bar: {
        borderRadius: '8px'
      },
      root: {
        borderRadius: '8px'
      }
    }
  },

  MuiMenuItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        [`&.${menuItemClasses.selected}`]: {
          backgroundColor: alpha(theme.palette.accent.main, 0.08),
          '&:hover': {
            backgroundColor: alpha(theme.palette.accent.main, 0.12)
          }
        }
      })
    }
  },

  MuiSelect: {
    defaultProps: {
      size: 'small'
    }
  },

  MuiStepIcon: {
    styleOverrides: {
      root: ({ theme }) => ({
        [`&.${stepIconClasses.active}`]: {
          color: theme.palette.accent.main
        },
        [`&.${stepIconClasses.completed}`]: {
          color: theme.palette.accent.main
        }
      })
    }
  },

  MuiSwitch: {
    defaultProps: {
      color: 'accent'
    }
  },

  MuiTablePagination: {
    styleOverrides: {
      actions: ({ theme }) => ({
        color: theme.palette.primary.main
      })
    }
  },

  MuiTextField: {
    defaultProps: {
      size: 'small'
    },
    styleOverrides: {
      root: ({ theme }) => ({
        '& label.Mui-focused': {
          color: theme.palette.accent.main
        }
      })
    }
  },

  MuiToggleButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: theme.palette.text.secondary,
        '&.Mui-selected': {
          color: theme.palette.common.white,
          backgroundColor: theme.palette.accent.main,
          '&:hover': {
            color: theme.palette.text.secondary
          }
        }
      })
    }
  },

  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        fontFamily,
        fontWeight: 400,
        fontSize: '14px',
        lineHeight: 20 / 14
      }
    }
  }
}

export default componentOverrides
